import { getAlgoliaResults } from '@algolia/autocomplete-js';
import { algoliasearch } from 'algoliasearch';
import { createBrowserLocalStorageCache, createFallbackableCache, createMemoryCache } from '@algolia/client-common';
import Autocomplete from './Autocomplete';
import SearchItem from './SearchItem';
import React, { useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// I Algolia api key or id is missing we are mocking the call because context is using it
// if the keys aren't available it defaults to our orginal search bar.
// Notes: consider adding a feature flag in the future
const createSearchClient = () => {
  const appId = process.env.REACT_APP_ALGOLIA_APP_ID ?? '';
  const apiKey = process.env.REACT_APP_ALGOLIA_API_KEY ?? '';

  if (appId && apiKey) {
    return algoliasearch(appId, apiKey, {
      responsesCache: createFallbackableCache({
        caches: [
          createBrowserLocalStorageCache({ key: 'algolia-responses' }),
          createMemoryCache()
        ]
      })
    }) as any;
  } else {
    return {
      transporter: {} as any,
      appId: '',
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      clearCache: () => { },
      _ua: '',
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      addAlgoliaAgent: () => { },
      initIndex: () => ({} as any),
      search: async () => ({
        results: [
          {
            hits: [],
            nbHits: 0,
            page: 0
          }
        ]
      }),
      searchForFacetValues: async () => [],
      getRecommendations: async () => ({ results: [] }),
      multiple: async () => ({ results: [] }),
      multipleGetObjects: async () => ({ results: [] }),
      multipleQueries: async () => ({ results: [] })
    } as any;
  }
};

export const searchClient = createSearchClient();

let debounceTimer: ReturnType<typeof setTimeout>;

function Core({ pathname }) {
  const [pc, setPc] = useState(true);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    screen.width < 900 ? setPc(false) : setPc(true);
  }, []);

  const handleShowAllResults = (query: string) => {
    history.push(`/search-results?q=${encodeURIComponent(query)}`);
  };

  const isCreativePage = location.pathname.includes('/creative');

  const urlParams = new URLSearchParams(location.search);
  const initialQuery = urlParams.get('q') ?? '';

  const updateSearchURL = useCallback((query: string) => {
    if (query) {
      history.replace(`/creatives?q=${encodeURIComponent(query)}`);
    } else {
      history.replace('/creatives');
    }
  }, [history]);

  return (
    <div
      className="navbar-search"
      style={{
        width: '500px',
        padding: '10px'
      }}
    >
      <style>
        {`
          .navbar-search .aa-Input {
            border-radius: 10px !important;
          }
          .navbar-search .aa-Form {
            border-radius: 10px !important;
          }
        `}
      </style>
      <Autocomplete
        openOnFocus={pc && !isCreativePage}
        placeholder={`Search for ${pathname}`}
        initialState={{
          query: initialQuery
        }}
        onSubmit={({ state }: any) => {
          if (isCreativePage && pathname === 'user') {
            updateSearchURL(state.query || '');
          }
        }}
        getSources={async ({ query }: { query: string }) => {
          return await new Promise((resolve) => {
            if (debounceTimer) {
              clearTimeout(debounceTimer);
            }
            debounceTimer = setTimeout(() => {
              if (isCreativePage && pathname === 'user') {
                resolve([
              {
                sourceId: 'users',
                getItems() {
                  return getAlgoliaResults({
                    searchClient,
                    queries: [
                      {
                        indexName: 'user',
                        params: {
                          query,
                          hitsPerPage: 4,
                          restrictSearchableAttributes: ['name']
                        }
                      }
                    ]
                  });
                },
                templates: {
                  item({ item, components }) {
                    return (
                      <SearchItem
                        hit={item}
                        components={components}
                        pathname="user"
                        searchQuery={query}
                        history={history}
                      />
                    );
                  }
                }
              },
              {
                sourceId: 'project-owners',
                getItems() {
                  return searchClient.search([
                    {
                      indexName: 'project',
                      query,
                      params: {
                        hitsPerPage: 20,
                        restrictSearchableAttributes: ['title']
                      }
                    }
                  ]).then(async (response: any) => {
                    const allProjects = response.results?.[0]?.hits || [];

                    const projects = allProjects.slice(0, 4);

                    if (projects.length === 0) {
                      return [];
                    }

                    const authorEmails = Array.from(new Set(projects.map((p: any) => p.authorEmail).filter(Boolean)));

                    if (authorEmails.length === 0) {
                      return [];
                    }

                    let users: any[] = [];

                    try {
                      const userSearchPromises = authorEmails.map(email =>
                        searchClient.search([
                          {
                            indexName: 'user',
                            query: email,
                            params: {
                              hitsPerPage: 1
                            }
                          }
                        ])
                      );

                      const userResponses = await Promise.all(userSearchPromises);

                      users = userResponses
                        .map((response: any) => response.results?.[0]?.hits?.[0])
                        .filter(Boolean);
                    } catch (error) {
                      console.error('Failed to fetch users by email:', error);
                    }

                    const emailToUser = new Map();
                    users.forEach((user: any) => {
                      emailToUser.set(user.email, user);
                    });

                    const seenEmails = new Set();
                    const results = projects.map((project: any) => {
                      const user = emailToUser.get(project.authorEmail);

                      if (user) {
                        if (seenEmails.has(user.email)) {
                          return null;
                        }
                        seenEmails.add(user.email);

                        const cleanHighlightResult = user._highlightResult ? {
                          ...user._highlightResult,
                          professionsCombined: {
                            value: user.professionsCombined || '',
                            matchLevel: 'none',
                            matchedWords: []
                          },
                          name: {
                            value: user.name || '',
                            matchLevel: 'none',
                            matchedWords: []
                          }
                        } : undefined;

                        return {
                          ...user,
                          _fromProject: true,
                          _matchedProjectTitle: project.title,
                          objectID: user.objectID || user.id,
                          _highlightResult: cleanHighlightResult
                        };
                      }

                      return null;
                    }).filter(Boolean);

                    return results;
                    }).catch((_error: any) => {
                      return [];
                    });
                },
                templates: {
                  item({ item, components }) {
                    return (
                      <SearchItem
                        hit={item}
                        components={components}
                        pathname="user"
                        searchQuery={query}
                        history={history}
                      />
                    );
                  }
                }
              },
              {
                sourceId: 'show-all-creatives',
                getItems() {
                  return query ? [{ objectID: 'show-all' }] : [];
                },
                templates: {
                  item() {
                    return (
                      <button
                        className="search-show-all-btn"
                        style={{
                          marginTop: '10px',
                          width: '100%',
                          padding: '10px',
                          backgroundColor: '#007bff',
                          color: '#fff',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => updateSearchURL(query)}
                      >
                        Show All Results
                      </button>
                    );
                  }
                }
              }
            ]);
            return;
          }

          resolve([
            {
              sourceId: pathname,
              getItems() {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: pathname,
                      params: {
                        query,
                        hitsPerPage: 8
                      }
                    }
                  ]
                });
              },
              templates: {
                item({ item, components }) {
                  return (
                    <SearchItem
                      hit={item}
                      components={components}
                      pathname={pathname}
                      searchQuery={query}
                      history={history}
                    />
                  );
                },
                noResults() {
                  return 'No results found!';
                },
                footer({ state }) {
                  // Hide the show all results button if we are searching for projects until the new page for displaying
                  // both creatives and projects together is created
                  if (!state.query || pathname === 'project') return null;

                  return (
                    <button
                      className="search-show-all-btn"
                      style={{
                        marginTop: '10px',
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleShowAllResults(state.query)}
                    >
                      Show All Results
                    </button>
                  );
                }
              }
            }
          ]);
        }, 300);
      });
    }}
      />
    </div>
  );
}

export default Core;
